import { Dialog, DialogActions, DialogContent } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { PageHeaderTitleOnly } from 'lib/designSystem/PageHeader';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import type { GetFactoringExtensionPendingAccountDetail } from 'lib/api/extension/factoring/types';

import type {
  ExtensionCheckboxesSetters,
  ExtensionCheckboxesState,
} from '../hooks/useExtensionCheckboxes';
import { buildExtensionRequest } from '../utils/buildExtensionRequest';
import { handleExportExtensionReport } from '../utils/exportExtensionReport';
import { ExtensionCheckboxes } from './ExtensionCheckboxes';

export interface ExtensionExportDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  extension: Record<string, unknown>;
  checkboxes: ExtensionCheckboxesState;
  setters: ExtensionCheckboxesSetters;
  /** Perfil de documento para montos en el PDF */
  document?: Record<string, unknown> | null;
  pendingAccounts?: GetFactoringExtensionPendingAccountDetail[];
}

export const ExtensionExportDialog: React.FC<ExtensionExportDialogProps> = ({
  open,
  setOpen,
  extension,
  checkboxes,
  setters,
  document,
  pendingAccounts,
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const onExport = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const request = buildExtensionRequest({
        extension,
        checkboxes,
        document,
        pendingAccounts,
      });
      await handleExportExtensionReport({ dispatch, user, request });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const extensionId = extension?.id != null ? String(extension.id) : '';

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogContent>
        <PageHeaderTitleOnly
          variant="dialogHeader"
          iconType="checklist"
          title={`Exportar reporte prórroga #${extensionId}`}
          subtitle="Selecciona qué secciones incluir en el PDF."
        />
        {loading ? (
          <div style={{ minHeight: 160 }}>
            <CenteredCircularProgress type="dialog" />
          </div>
        ) : (
          <ExtensionCheckboxes checkboxes={checkboxes} setters={setters} />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={() => void onExport()}
          disabled={loading}
          loading={loading}
        >
          Exportar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
